import React, {useEffect, useRef, useState} from 'react';
import {flushSync} from 'react-dom';
import {
  Button,
  Form,
  Popconfirm,
  Select,
  Table,
  Col,
  Row,
  Divider,
  Input,
  Checkbox,
  Modal,
  Tabs,
  Tag,
  Tooltip,
  Alert,
} from 'antd';
import {CSVLink} from 'react-csv';
import {
  attendingDinnerLabel,
  calculateProcessingFeeForRegistrations,
  DescriptionItem,
  EventRegistrationStatus,
  OnlyAlphabetInputValidatorWithSpecialCharacters,
  PageSizeArray,
  PaginationPosition,
  ReturnNAOnEmpty,
  eventRegistrationsFilters,
  event_registration_type,
  formatCurrencyByLocalChapter,
  formatDate,
  formatUtcToLocalDateTime,
  getStatusInfoOfEventRegistration,
  hasProcessingFeeExplicitlyDisabled,
  meetingPresentationLabel,
  registrationFilterOptions,
  shouldResolveDuplicateToBePaidOnStatusChange,
  findDuplicateToBePaidRegistrationsInList,
  shouldConfirmOverCapacityOnStatusChange,
  findOverCapacityEventOptionsForStatusChange,
  EventRegistrationAdminTab,
  buildSponsorTreeRows,
  flattenSponsorRegistrationsWithReps,
  selectRowsWithSponsorVendorReps,
  isInPersonCapacityFull,
} from '../../../Util/commonUtility';
import {showDuplicateRegistrationWarningModal} from './showDuplicateRegistrationWarningModal';
import {showOverCapacityWarningModal} from './showOverCapacityWarningModal';
import {showSpotOpenedModal} from './showSpotOpenedModal';
import {FilterOutlined, QuestionCircleOutlined} from '@ant-design/icons';
import DynamicSearchForm from '../../CommonComponents/DynamicSearch';
import CommonTableColumnTextView from '../../CommonComponents/CommonTableColumnTextView';
import AdminActionsDropdown from '../../CommonComponents/AdminActionsDropdown/AdminActionsDropdown';
import ViewportSelect from '../../CommonComponents/ViewportPopup/ViewportSelect';
import {useNavigate, useParams} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import AdminViewDetailsDrawer from '../../CommonComponents/AdminViewDetailsDrawer';
import CommonUserModal from './../../CommonComponents/CommonUserModal/index';
import {memorizedInitiatedRefundAttendees} from '../../../Store/Actions/eventRegister';
import {editRefundStatusById} from '../../../Store/Actions/events';
import {Option} from 'antd/es/mentions';
import {usePermissions} from '../../../hooks/usePermission';
import {getAllChapters} from '../../../Store/Actions';
import {getEventsById} from '../../../Store/Actions/events';
import {FETCH_END, FETCH_START} from '../../../Constant/ActionTypes';
import useLocalization from '../../../Localization';
import './sponsorTree.css';

const JAPAN_LOCAL_CHAPTER_NAME = 'japan';

const AddNoteModal = ({
  visible,
  onCancel,
  onOk,
  noteValue,
  setNoteValue,
  selectedEventRegistrationValue,
  ApplicationString,
  record,
}) => {
  return (
    <Modal
      title={
        <div style={{paddingBottom: '8px'}}>
          <div style={{fontSize: '20px', fontWeight: 600, color: '#005d61'}}>
            {record?.note
              ? ApplicationString['addNoteModal.editNote']
              : ApplicationString['addNoteModal.addNote']}
          </div>
          <div
            style={{
              fontSize: '14px',
              color: '#747474',
              fontWeight: 400,
              marginTop: '4px',
            }}>
            {ApplicationString['addNoteModal.eventRegistrationId']}:{' '}
            {selectedEventRegistrationValue}
          </div>
        </div>
      }
      open={visible}
      onCancel={onCancel}
      onOk={onOk}
      okText={
        record?.note
          ? ApplicationString['addNoteModal.editNote']
          : ApplicationString['addNoteModal.addNote']
      }
      cancelText={ApplicationString['common.button.backForm']}
      width={650}
      centered
      okButtonProps={{
        style: {
          backgroundColor: '#005d61',
          color: 'white',
          borderColor: '#005d61',
          height: '40px',
          padding: '0 24px',
          fontWeight: 500,
        },
      }}
      cancelButtonProps={{
        style: {
          borderColor: '#005d61',
          color: '#005d61',
          height: '40px',
          padding: '0 24px',
          fontWeight: 500,
        },
      }}
      maskClosable={false}>
      <div style={{padding: '8px 0 16px 0'}}>
        <div
          style={{
            marginBottom: '12px',
            fontSize: '15px',
            fontWeight: 500,
            color: '#3c3c3c',
          }}>
          Note <span style={{color: '#ff4d4f'}}>*</span>
        </div>
        <Input.TextArea
          rows={6}
          placeholder={ApplicationString['addNoteModal.placeholder']}
          value={noteValue}
          onChange={(e) => setNoteValue(e.target.value)}
          maxLength={1000}
          showCount
          style={{
            resize: 'vertical',
            fontSize: '14px',
          }}
          autoFocus
        />
        <div
          style={{
            marginTop: '8px',
            fontSize: '13px',
            color: '#747474',
            fontStyle: 'italic',
          }}>
          {ApplicationString['addNoteModal.description']}
        </div>
      </div>
    </Modal>
  );
};

const isHttpUrl = (value) =>
  typeof value === 'string' && /^https?:\/\//i.test(value.trim());

const renderAnswerValue = (value) => {
  if (!value) return 'N/A';
  const parts = String(value)
    .split(', ')
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length > 1 && parts.every(isHttpUrl)) {
    return parts.map((url, index) => (
      <span key={`${url}-${index}`}>
        {index > 0 ? ', ' : null}
        <a href={url} target='_blank' rel='noopener noreferrer'>
          {url}
        </a>
      </span>
    ));
  }
  if (isHttpUrl(value)) {
    return (
      <a href={value} target='_blank' rel='noopener noreferrer'>
        {value}
      </a>
    );
  }
  return value;
};

const normalizeQuestionLabel = (label) =>
  String(label || '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();

// One entry per question; a label repeated within the same form gets a " (2)" suffix.
const getQualificationQuestionAnswers = (answers) => {
  if (!Array.isArray(answers)) return [];
  const seen = new Map();
  return answers
    .filter(
      (answer) =>
        answer?.type !== 'HIDDEN_FIELDS' && String(answer?.label || '').trim(),
    )
    .map((answer) => {
      const label = String(answer.label).trim().replace(/\s+/g, ' ');
      const normalized = normalizeQuestionLabel(label);
      const count = (seen.get(normalized) || 0) + 1;
      seen.set(normalized, count);
      return {
        id: count > 1 ? `${normalized} (${count})` : normalized,
        label: count > 1 ? `${label} (${count})` : label,
        value: answer.value ?? '',
      };
    });
};

// Registrations can come from options linked to different Tally forms, so columns are
// the union of questions across all rows (first-seen order); same question text shares a column.
const buildQualificationQuestionColumns = (registrations) => {
  const columns = new Map();
  (registrations || []).forEach((item) => {
    getQualificationQuestionAnswers(item?.qualification_form_answers).forEach(
      ({id, label}) => {
        if (!columns.has(id)) {
          // react-csv treats dots in keys as paths, so use index-based keys.
          columns.set(id, {label: `Q: ${label}`, key: `q_${columns.size}`});
        }
      },
    );
  });
  return columns;
};

const QualificationFormModal = ({
  visible,
  onCancel,
  record,
  qualificationMeta,
}) => {
  const answers = Array.isArray(record?.qualification_form_answers)
    ? record.qualification_form_answers
    : [];

  return (
    <Modal
      title={
        <div style={{paddingBottom: '8px'}}>
          <div style={{fontSize: '20px', fontWeight: 600, color: '#005d61'}}>
            Qualification Form
          </div>
          <div
            style={{
              fontSize: '14px',
              color: '#747474',
              fontWeight: 400,
              marginTop: '4px',
            }}>
            Registration ID: {record?.id || 'N/A'}
          </div>
        </div>
      }
      open={visible}
      onCancel={onCancel}
      footer={[
        <Button key='close' onClick={onCancel}>
          Close
        </Button>,
      ]}
      width={650}
      centered
      maskClosable={false}>
      <div style={{padding: '8px 0 8px 0'}}>
        <Row gutter={[16, 12]} style={{marginBottom: 16}}>
          <Col span={12}>
            <DescriptionItem
              title='Status'
              content={
                <span style={{color: qualificationMeta?.color}}>
                  {qualificationMeta?.label || 'N/A'}
                </span>
              }
            />
          </Col>
          <Col span={12}>
            <DescriptionItem
              title='Submitted At'
              content={
                qualificationMeta?.submittedAt
                  ? formatUtcToLocalDateTime(qualificationMeta.submittedAt) ||
                    'N/A'
                  : 'N/A'
              }
            />
          </Col>
          <Col span={24}>
            <DescriptionItem
              title='Tally Submission ID'
              content={qualificationMeta?.tallySubmissionId || 'N/A'}
            />
          </Col>
        </Row>

        <Divider style={{margin: '8px 0 16px'}} />

        {answers.length > 0 ? (
          <div style={{display: 'flex', flexDirection: 'column', gap: 12}}>
            {answers.map((answer, index) => (
              <div
                key={`${answer.label}-${index}`}
                style={{
                  padding: '10px 12px',
                  background: '#fafafa',
                  border: '1px solid #f0f0f0',
                  borderRadius: 6,
                }}>
                <div
                  style={{
                    fontSize: 12,
                    color: '#8c8c8c',
                    marginBottom: 4,
                    fontWeight: 500,
                  }}>
                  {answer.label}
                </div>
                <div
                  style={{
                    fontSize: 14,
                    color: '#262626',
                    wordBreak: 'break-word',
                  }}>
                  {renderAnswerValue(answer.value)}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{color: '#8c8c8c', fontSize: 14}}>
            {qualificationMeta?.submittedAt
              ? 'No saved answer fields for this submission. Re-submit the form after this update to capture answers, or open the Tally submissions dashboard.'
              : qualificationMeta?.requiresForm
              ? 'Qualification form has not been submitted yet.'
              : 'Qualification form is not required for this registration option.'}
          </div>
        )}

        {qualificationMeta?.tallyFormUrl ? (
          <div style={{marginTop: 16, fontSize: 13, color: '#8c8c8c'}}>
            Tally form template (empty form):{' '}
            <a
              href={qualificationMeta.tallyFormUrl}
              target='_blank'
              rel='noopener noreferrer'>
              {qualificationMeta.tallyFormUrl}
            </a>
          </div>
        ) : null}
      </div>
    </Modal>
  );
};

const EventRegistrationsComponent = (props) => {
  const {
    allEventRegistrationData,
    handlePaginationChange,
    foodOptions,
    allEventRegistrationDataForCSV,
    registrationOptions,
    eventOptionsByType,
    UpdateRegistrationOptions,
    totalPaidAmount,
    totalDiscountedAmount,
    totalCreditAmount,
    totalRefundAmount,
    paging,
    sortParams,
    handleTableChange,
    cancelEventRegistrations,
    searchValues,
    fetchEventRegistrations,
    fetchEventRegistrationsForCSV,
    csvDataLoading,
    eventLocalChapterId,
    addNoteByEventRegistration,
    deleteToBePaidRegistration,
    registrationTab = EventRegistrationAdminTab.registrations,
    onRegistrationTabChange,
  } = props;
  const isSponsorsTab = registrationTab === EventRegistrationAdminTab.sponsors;
  // Sponsors tab: each sponsor row expands to its vendor reps (from every payment).
  const tableRows = isSponsorsTab
    ? buildSponsorTreeRows(allEventRegistrationData)
    : allEventRegistrationData;
  // Every registration on the page (sponsors + their reps), for lookups by id.
  const allRowsOnPage = isSponsorsTab
    ? flattenSponsorRegistrationsWithReps(allEventRegistrationData)
    : allEventRegistrationData;
  const [expandedSponsorKeys, setExpandedSponsorKeys] = useState(null);
  const ps = useSelector(
    ({pageSizes}) => pageSizes.pageSizes.eventRegistrations,
  );
  const pn = useSelector(
    ({pageSizes}) => pageSizes.pageNumbers.eventRegistrations,
  );
  const {id} = useParams();
  const {ApplicationString} = useLocalization();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [expanded, setExpanded] = useState(false);
  const [isAddNoteModalVisible, setIsAddNoteModalVisible] = useState(false);
  const [noteValue, setNoteValue] = useState('');
  const [editingKey, setEditingKey] = useState(null);
  const [isSavingRegistration, setIsSavingRegistration] = useState(false);
  const savingRegistrationRef = useRef(false);
  const savingButtonRef = useRef(null);
  const isEditing = (record) =>
    editingKey != null && String(record.id) === String(editingKey);
  const [selectedEventRegistration, setSelectedEventRegistration] = useState({
    id: [],
    note_id: '',
  });
  const [
    selectedEventRegistrationWithRecord,
    setSelectedEventRegistrationWithRecord,
  ] = useState();
  const [selectionType, setSelectionType] = useState('checkbox');
  const [isCancelAndRefundAccessed, setIsCancelAndRefundAccessed] =
    useState(true);

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [showApproveCancelRegistration, setShowApproveCancelRegistration] =
    useState(false);
  const [open, setOpen] = useState(false);
  const [openDrawerDetails, setOpenDrawerDetails] = useState(false);
  const [qualificationFormModalVisible, setQualificationFormModalVisible] =
    useState(false);
  const [qualificationFormRecord, setQualificationFormRecord] = useState(null);
  const [attendeePresentationChecked, setAttendeePresentationChecked] =
    useState(false);
  const [attendeeDinnerChecked, setAttendeeDinnerChecked] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  let totalIncome = 0;
  let totalDiscounted = 0;
  let totalPaidAmountWithProcessingFeeForCSV = 0;
  let totalProcessingFeeForCSV = 0;

  const userInfo = JSON.parse(localStorage.getItem('userInfo'));
  const userRole = userInfo?.role;
  const {checkPermission} = usePermissions(userRole);
  const {getEventsData} = useSelector(({events}) => events);
  const allChapters = useSelector((state) => state.chapters.allChapters);
  // Event's local chapter from events API (has id + name)
  const eventLocalChapterFromEvent = getEventsData?.data?.local_chapter_id;

  const localChapterData = allChapters?.data?.chapters?.find(
    (obj) => obj.id === eventLocalChapterId,
  );
  const isJapanLocalChapter =
    (
      eventLocalChapterFromEvent?.name || localChapterData?.name
    )?.toLowerCase() === JAPAN_LOCAL_CHAPTER_NAME;
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (id) {
      dispatch(getEventsById(id));
    }
  }, [id]);

  useEffect(() => {
    if (!localChapterData) {
      dispatch(getAllChapters());
    }
  }, [localChapterData]);

  useEffect(() => {
    if (
      !checkPermission('events_registrations', 'cancelAndRefundRegistration')
    ) {
      setIsCancelAndRefundAccessed(false);
    }
  }, []);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, []);

  // Drawer functions ----------------------------------------------------
  const showDrawer = (record) => {
    setOpenDrawerDetails(record);
    setOpen(true);
  };

  const showQualificationFormModal = (record) => {
    setQualificationFormRecord(record);
    setQualificationFormModalVisible(true);
  };

  const closeQualificationFormModal = () => {
    setQualificationFormModalVisible(false);
    setQualificationFormRecord(null);
  };

  const onCloseDrawer = () => {
    setOpen(false);
  };

  const total_Amount =
    totalPaidAmount +
    totalCreditAmount +
    totalDiscountedAmount -
    totalRefundAmount;

  const getProcessingFee = (record) => {
    const paidAmount = Number(record?.paid_amount || 0);
    if (paidAmount <= 0) return 0;
    if (hasProcessingFeeExplicitlyDisabled(record)) return 0;

    const transactionProcessingFee = Number(record?.processing_fee_amount || 0);
    const transactionBaseAmount = Number(record?.payment_base_amount || 0);
    if (transactionProcessingFee > 0 && transactionBaseAmount > 0) {
      return (
        Math.round(
          (paidAmount / transactionBaseAmount) * transactionProcessingFee * 100,
        ) / 100
      );
    }

    const recordWithoutTransactionFee = {
      ...record,
      processing_fee_amount: undefined,
      processing_fee: undefined,
      payment_processing_fee: undefined,
      transaction_fee: undefined,
      card_processing_fee: undefined,
    };

    return calculateProcessingFeeForRegistrations(
      [
        {
          ...recordWithoutTransactionFee,
          total_cost: paidAmount,
          discount_amount: 0,
        },
      ],
      recordWithoutTransactionFee,
    );
  };

  const getPaidAmountWithProcessingFee = (record) => {
    const paidAmount = Number(record?.paid_amount || 0);
    if (paidAmount <= 0) return 0;

    return Math.round((paidAmount + getProcessingFee(record)) * 100) / 100;
  };

  const getQualificationFormMeta = (record) => {
    const registrationOption = record?.event_registration_options?.registration;
    const requiresForm =
      registrationOption?.requires_qualification_form === true;
    const tallyFormUrl = registrationOption?.tally_form_url || null;
    const submittedAt = record?.qualification_form_submitted_at || null;
    const tallySubmissionId = record?.tally_submission_id || null;

    if (!requiresForm) {
      return {
        requiresForm: false,
        label: 'Not required',
        color: '#8c8c8c',
        submittedAt,
        tallySubmissionId,
        tallyFormUrl,
      };
    }

    if (submittedAt) {
      return {
        requiresForm: true,
        label: 'Submitted',
        color: '#389e0d',
        submittedAt,
        tallySubmissionId,
        tallyFormUrl,
      };
    }

    return {
      requiresForm: true,
      label: 'Pending',
      color: '#d46b08',
      submittedAt: null,
      tallySubmissionId,
      tallyFormUrl,
    };
  };

  const EventRegistrationDrawerContent = () => {
    const {
      attendee_first_name,
      attendee_last_name,
      user_id,
      event_id,
      attendee_email,
      attendee_company,
      attendee_department,
      attendee_job_title,
      is_sponsor,
      is_reps,
      registration_type,
      event_registration_options,
      total_cost,
      discount_amount,
      discount_code,
      status,
      created_at,
      user_local_chapter,
      payment_id,
      payment_country,
      payment_zip,
      payment_date,
      card_last4,
    } = openDrawerDetails || {};

    return (
      <>
        <Row>
          <Col span={24}>
            <DescriptionItem
              title='Attendee Status'
              content={
                {
                  3: 'Paid',
                  4: 'Payment Confirmed',
                  6: 'Fee Wavier',
                  '-1': 'To be Deleted',
                  0: 'Payment Refunded',
                  1: 'To be Paid',
                  2: 'Refund Pending',
                  5: 'No Charge Event',
                }[status] || status
              }
            />
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col span={12}>
            <DescriptionItem title='User Id' content={user_id} />
          </Col>
          <Col span={12}>
            <DescriptionItem title='Event Id' content={event_id} />
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col span={12}>
            <DescriptionItem title='First Name' content={attendee_first_name} />
          </Col>
          <Col span={12}>
            <DescriptionItem title='Last Name' content={attendee_last_name} />
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col span={12}>
            <DescriptionItem title='Attendee Email' content={attendee_email} />
          </Col>
          <Col span={12}>
            <DescriptionItem
              title='Attendee Company'
              content={attendee_company}
            />
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col span={12}>
            <DescriptionItem
              title='Department'
              content={attendee_department || 'N/A'}
            />
          </Col>
          <Col span={12}>
            <DescriptionItem
              title='Job Title'
              content={attendee_job_title || 'N/A'}
            />
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col span={12}>
            <DescriptionItem
              title='Sponsorship'
              content={is_sponsor ? 'Yes' : 'No'}
            />
          </Col>
          <Col span={12}>
            <DescriptionItem
              title='Representative'
              content={is_reps ? 'Yes' : 'No'}
            />
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col span={24}>
            <DescriptionItem
              title='Registration Type'
              content={registration_type}
            />
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col span={24}>
            <DescriptionItem
              title='Registration Option'
              content={
                event_registration_options?.sponsorship?.name
                  ? event_registration_options?.sponsorship?.name
                  : event_registration_options?.registration?.name +
                    ' ' +
                    '-' +
                    ' ' +
                    formatCurrencyByLocalChapter(
                      event_registration_options?.registration?.price || 0,
                      localChapterData,
                    )
              }
            />
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col span={24}>
            <DescriptionItem
              title='Food Option'
              content={
                event_registration_options?.food?.name
                  ? event_registration_options?.food?.name +
                    ' ' +
                    '-' +
                    ' ' +
                    formatCurrencyByLocalChapter(
                      event_registration_options?.food?.price || 0,
                      localChapterData,
                    )
                  : 'No Food'
              }
            />
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col span={12}>
            <DescriptionItem
              title='Total Cost'
              content={formatCurrencyByLocalChapter(
                total_cost ? total_cost : 0,
                localChapterData,
              )}
            />
          </Col>
          <Col span={12}>
            <DescriptionItem
              title='Discounted Cost'
              content={formatCurrencyByLocalChapter(
                discount_amount ? discount_amount : 0,
                localChapterData,
              )}
            />
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col span={12}>
            <DescriptionItem
              title='Total Cost After Discount'
              content={formatCurrencyByLocalChapter(
                total_cost - discount_amount || 0,
                localChapterData,
              )}
            />
          </Col>
          <Col span={12}>
            <DescriptionItem
              title='Coupon Code Applied'
              content={discount_code || 'N/A'}
            />
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col span={12}>
            <DescriptionItem
              title='Registered Date'
              content={formatDate(created_at)}
            />
          </Col>
          <Col span={12}>
            <DescriptionItem
              title='User Local Chapter'
              content={user_local_chapter}
            />
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col span={12}>
            <DescriptionItem
              title='Stripe Payment ID'
              content={payment_id || 'N/A'}
            />
          </Col>
          <Col span={12}>
            <DescriptionItem
              title='Stripe Payment Date'
              content={
                payment_date
                  ? formatUtcToLocalDateTime(payment_date) || 'N/A'
                  : 'N/A'
              }
            />
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col span={12}>
            <DescriptionItem
              title='Credit Card Number'
              content={card_last4 ? `**** **** **** ${card_last4}` : 'N/A'}
            />
          </Col>
          <Col span={12}></Col>
        </Row>
        <Divider />
        <Row>
          <Col span={12}>
            <DescriptionItem
              title='Billing Country'
              content={payment_country || 'N/A'}
            />
          </Col>
          <Col span={12}>
            <DescriptionItem
              title='Billing ZIP'
              content={payment_zip || 'N/A'}
            />
          </Col>
        </Row>
        <Divider />
      </>
    );
  };

  useEffect(() => {
    searchValues && setExpanded(true);
  }, [searchValues]);

  const handleCancelGroupOfRegistration = async () => {
    const eventIds = {
      eventRegistrationIds: selectedEventRegistration.id,
    };
    const result = await cancelEventRegistrations(eventIds, setSelectedRowKeys);
    setSelectedEventRegistration({
      ...selectedEventRegistration,
      id: [],
    });
    if (result?.spotOpened) {
      showSpotOpenedModal();
    }
  };
  const handleInitiateRefund = () => {
    if (
      !selectedEventRegistration.id ||
      selectedEventRegistration.id?.length <= 0
    ) {
      dispatch({type: FETCH_ERROR, payload: 'Please select attendee(s)'});
      return;
    }
    const refundableRows = (selectedEventRegistrationWithRecord || []).filter(
      (row) => Number(row?.paid_amount || 0) > 0,
    );
    if (refundableRows.length <= 0) {
      dispatch({
        type: FETCH_ERROR,
        payload:
          'Refund can only be initiated for registrations with a paid amount greater than $0. Use Cancel for free / $0 vendor reps.',
      });
      return;
    }
    const refundableIds = refundableRows.map((row) => row.id);
    dispatch(memorizedInitiatedRefundAttendees(refundableRows));
    const Data = {
      eventRegistrationIds: refundableIds,
      status: 2,
    };
    dispatch(editRefundStatusById(Data));
    navigate(`/admin/eventRefund/${id}`);
  };

  const handleInitiateRefundVisible = () => {
    const hasRefundable = (selectedEventRegistrationWithRecord || []).some(
      (row) => Number(row?.paid_amount || 0) > 0,
    );
    if (!hasRefundable) {
      dispatch({
        type: FETCH_ERROR,
        payload:
          'Refund can only be initiated for registrations with a paid amount greater than $0. Use Cancel for free / $0 vendor reps.',
      });
      return;
    }
    setIsModalVisible(true);
  };
  const hideModal = () => {
    setIsModalVisible(false);
  };

  useEffect(() => {
    if (selectedRowKeys?.length > 0) {
      setShowApproveCancelRegistration(true);
      setSelectedEventRegistration({
        ...selectedEventRegistration,
        id: selectedRowKeys,
      });
    } else {
      setShowApproveCancelRegistration(false);
      setSelectedEventRegistration({
        ...selectedEventRegistration,
        id: [],
      });
    }
  }, [selectedRowKeys]);

  const isRefundSelectionDisabled = (record) => {
    const disabledStatuses = [
      EventRegistrationStatus.PAYMENT_REFUNDED,
      EventRegistrationStatus.TO_BE_DELETED,
      EventRegistrationStatus.REFUND_PENDING,
      EventRegistrationStatus.TO_BE_PAID,
    ];
    return disabledStatuses.includes(Number(record?.status));
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowIds, selectedKeys) => {
      const rows = isSponsorsTab
        ? selectRowsWithSponsorVendorReps(
            selectedRowKeys,
            selectedKeys,
            allRowsOnPage,
            isRefundSelectionDisabled,
          )
        : selectedKeys;
      setSelectedRowKeys(rows?.map((row) => row.id));
      setSelectedEventRegistrationWithRecord(rows);
    },
    getCheckboxProps: (record) => {
      // $0 rows stay selectable for Cancel; Initiate Refund filters paid_amount > 0.
      return {
        disabled: isRefundSelectionDisabled(record),
      };
    },
  };

  const footer = () => {
    return (
      <div>
        {showApproveCancelRegistration && (
          <div className='admin-registration-container'>
            <Popconfirm
              title='Cancel Selected Registration'
              description={
                isSponsorsTab
                  ? 'Are you sure you want to cancel the selected registrations? Cancelling a sponsor also cancels all of its vendor reps.'
                  : 'Are you sure you want to cancel the selected registrations?'
              }
              onConfirm={handleCancelGroupOfRegistration}
              okType='primary'
              icon={<QuestionCircleOutlined style={{color: 'warning'}} />}>
              <div className='common-cancel-button'>
                <Button
                  id='EventRefundComponent-cancelRegistration'
                  className='common-submit-login common-form-submit'>
                  Proceed to Cancel Selected Registration
                </Button>
              </div>
            </Popconfirm>
            <CommonUserModal
              visible={isModalVisible}
              onCancel={hideModal}
              onConfirm={handleInitiateRefund}
              confirmBtnText={'Proceed'}
              content={
                <React.Fragment>
                  Are you sure you want to initiate a refund for the selected
                  registration(s)?
                </React.Fragment>
              }
              usedForAdminSide={true}
            />

            <div className='common-cancel-button'>
              <Button
                onClick={handleInitiateRefundVisible}
                id='EventRefundComponent-cancelRegistration'
                className='common-submit-login common-form-submit'>
                Initiate Refund
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  };
  const handleAttendeePresentationChange = (e) => {
    setAttendeePresentationChecked(e.target.checked);
  };
  const handleAttendeeDinnerChange = (e) => {
    setAttendeeDinnerChecked(e.target.checked);
  };
  const toggleExpand = () => {
    setExpanded(!expanded);
  };
  const handleSearch = (values) => {
    fetchEventRegistrations(values);
  };

  const handleOpenAddNoteModal = (record) => {
    setIsAddNoteModalVisible(true);
    setSelectedEventRegistration({
      ...selectedEventRegistration,
      note_id: record.id,
    });
    setNoteValue(record?.note || '');
  };

  const edit = (record) => {
    setAttendeeDinnerChecked(record?.attending_dinner);
    setAttendeePresentationChecked(record?.attending_presentation);
    const EditData = {
      ...record,
      food_option: record.event_registration_options?.food
        ? record.event_registration_options?.food?.event_option_id
        : 'no-food',
      registration_option:
        record.event_registration_options?.registration?.event_option_id,
    };
    form.setFieldsValue({
      ...EditData,
    });
    setEditingKey(record.id);
  };

  const exitEditMode = () => {
    setEditingKey(null);
    form.resetFields();
  };

  const cancel = () => {
    if (savingRegistrationRef.current) {
      return;
    }
    exitEditMode();
  };

  const releaseSavingButton = () => {
    const button = savingButtonRef.current;
    savingButtonRef.current = null;
    if (button) {
      button.disabled = false;
    }
  };

  const save = async (record) => {
    if (savingRegistrationRef.current) {
      return;
    }
    // Ignore further clicks immediately. Flush only the page loader here.
    // Updating this table in the same turn delays that paint, so the row
    // button state is applied on the next frame instead.
    savingRegistrationRef.current = true;

    let saveRequestStarted = false;
    const registrationId = record?.event_registration_options?.registration?.id;
    const foodId = record?.event_registration_options?.food?.id;

    try {
      flushSync(() => {
        dispatch({type: FETCH_START});
      });
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (savingRegistrationRef.current) {
            setIsSavingRegistration(true);
          }
        });
      });

      const values = await form.validateFields();

      if (isJapanLocalChapter) {
        const department = values?.attendee_department?.toString()?.trim();
        const jobTitle = values?.attendee_job_title?.toString()?.trim();
        const japanFieldErrors = [];
        if (!department) {
          japanFieldErrors.push({
            name: 'attendee_department',
            errors: ['Please fill out department'],
          });
        }
        if (!jobTitle) {
          japanFieldErrors.push({
            name: 'attendee_job_title',
            errors: ['Please fill out job title'],
          });
        }
      }

      const updateOptions = {
        food_option:
          values.food_option === 'no-food'
            ? {}
            : {id: foodId, event_option_id: values.food_option},
        ...(values.registration_option && {
          registration_option: {
            id: registrationId ?? '',
            event_option_id: values.registration_option,
          },
        }),
        ...(values.attending_presentation && {
          attending_presentation: attendeePresentationChecked,
        }),
        ...(values.attending_dinner && {
          attending_dinner: attendeeDinnerChecked,
        }),
        registration_type: values.registration_type,
        attendee_email: values.attendee_email,
        attendee_first_name: values.attendee_first_name,
        attendee_last_name: values.attendee_last_name,
        attendee_company: values.attendee_company,
        attendee_department: values.attendee_department,
        attendee_job_title: values.attendee_job_title,
        status: values.status,
      };

      const attendeeEmail = values.attendee_email || record.attendee_email;
      const duplicateRegistrations = findDuplicateToBePaidRegistrationsInList(
        allRowsOnPage,
        record,
        attendeeEmail,
      );
      const requiresDuplicateConfirmation =
        values.status !== undefined &&
        values.status !== record.status &&
        shouldResolveDuplicateToBePaidOnStatusChange(
          record.status,
          values.status,
        ) &&
        duplicateRegistrations.length > 0;

      const overCapacityOptions = findOverCapacityEventOptionsForStatusChange(
        record,
        eventOptionsByType,
      );
      const registrationType = (
        values.registration_type ||
        record.registration_type ||
        ''
      )
        .toString()
        .trim()
        .toLowerCase();
      const occupiesInPersonSeat = record?.is_reps
        ? Boolean(record?.attending_presentation || values.attending_presentation)
        : !record?.is_sponsor && registrationType !== 'webcast';
      const inPersonCapacityFull =
        isInPersonCapacityFull(eventOptionsByType?.in_person_remaining) &&
        occupiesInPersonSeat;
      const inPersonCapacityWarning = inPersonCapacityFull
        ? `This event's in-person capacity is full (${eventOptionsByType?.in_person_used} / ${eventOptionsByType?.in_person_capacity}). Proceeding will increase the in-person capacity by one and confirm this registration.`
        : '';
      const requiresOverCapacityConfirmation =
        values.status !== undefined &&
        values.status !== record.status &&
        shouldConfirmOverCapacityOnStatusChange(record.status, values.status) &&
        (overCapacityOptions.length > 0 || inPersonCapacityFull);

      const performSave = async ({
        confirmDeleteDuplicates = false,
        confirmOverCapacity = false,
      } = {}) => {
        saveRequestStarted = true;
        dispatch({type: FETCH_START});
        const payload = {
          ...updateOptions,
          ...(confirmDeleteDuplicates ? {confirm_delete_duplicates: true} : {}),
          ...(confirmOverCapacity ? {confirm_over_capacity: true} : {}),
        };
        const result = await UpdateRegistrationOptions(
          payload,
          record?.id,
          exitEditMode,
        );

        if (result?.success) {
          exitEditMode();
          return true;
        }

        if (result?.requiresDuplicateConfirmation && !confirmDeleteDuplicates) {
          await showDuplicateRegistrationWarningModal({
            fallbackMessage: result.errorMessage,
            onConfirm: () =>
              performSave({confirmDeleteDuplicates: true, confirmOverCapacity}),
          });
          return false;
        }

        if (result?.requiresOverCapacityConfirmation && !confirmOverCapacity) {
          await showOverCapacityWarningModal({
            fallbackMessage: result.errorMessage,
            onConfirm: () =>
              performSave({confirmDeleteDuplicates, confirmOverCapacity: true}),
          });
          return false;
        }

        return false;
      };

      const promptAndSave = async (flags = {}) => {
        if (requiresDuplicateConfirmation && !flags.confirmDeleteDuplicates) {
          dispatch({type: FETCH_END});
          const confirmed = await showDuplicateRegistrationWarningModal({
            fromStatusLabel: getStatusName(record.status),
            toStatusLabel: getStatusName(values.status),
            attendeeEmail,
            duplicateCount: duplicateRegistrations.length,
            duplicateRegistrations,
            onConfirm: async () => true,
          });
          if (!confirmed) {
            return;
          }
          flags.confirmDeleteDuplicates = true;
          dispatch({type: FETCH_START});
        }

        if (requiresOverCapacityConfirmation && !flags.confirmOverCapacity) {
          dispatch({type: FETCH_END});
          const confirmed = await showOverCapacityWarningModal({
            fromStatusLabel:
              overCapacityOptions.length > 0
                ? getStatusName(record.status)
                : undefined,
            toStatusLabel:
              overCapacityOptions.length > 0
                ? getStatusName(values.status)
                : undefined,
            overCapacityOptions,
            fallbackMessage: inPersonCapacityWarning || undefined,
            onConfirm: async () => true,
          });
          if (!confirmed) {
            return;
          }
          flags.confirmOverCapacity = true;
          dispatch({type: FETCH_START});
        }

        await performSave(flags);
      };

      await promptAndSave();
    } catch (err) {
      console.error('Validate Failed:', err);
    } finally {
      if (!saveRequestStarted) {
        dispatch({type: FETCH_END});
      }
      savingRegistrationRef.current = false;
      setIsSavingRegistration(false);
      releaseSavingButton();
    }
  };

  const handleAddNoteModalCancel = () => {
    setIsAddNoteModalVisible(false);
  };

  const handleAddNoteModalOk = () => {
    addNoteByEventRegistration(selectedEventRegistration.note_id, noteValue);
    setIsAddNoteModalVisible(false);
    setNoteValue('');
  };

  const EventRegistrationStatusObj = [
    {
      label: 'Registration Cancelled',
      value: EventRegistrationStatus.TO_BE_DELETED,
    },
    {
      label: 'Payment Refunded',
      value: EventRegistrationStatus.PAYMENT_REFUNDED,
    },
    {label: 'To be Paid', value: EventRegistrationStatus.TO_BE_PAID},
    {label: 'Refund Pending', value: EventRegistrationStatus.REFUND_PENDING},
    {label: 'Paid', value: EventRegistrationStatus.PAID},
    {
      label: 'Payment Confirmed',
      value: EventRegistrationStatus.PAYMENT_CONFIRMED,
    },
    {label: 'No Charge Event', value: EventRegistrationStatus.NO_CHARGE_EVENT},
    {label: 'Fee Waiver', value: EventRegistrationStatus.FEE_WAIVER},
    {
      label: 'Payment Processing',
      value: EventRegistrationStatus.PAYMENT_PROCESSING,
    },
  ];

  const getStatusName = (statusId) => {
    if (statusId === undefined || statusId === null || statusId === '') {
      return '';
    }
    const statusObj = EventRegistrationStatusObj.find(
      (status) => status.value === Number(statusId),
    );
    return statusObj ? statusObj.label : '';
  };
  const shouldEditCell = (column, record) => {
    if (column.dataIndex === 'registration_option') {
      return !record.is_reps && !record.is_sponsor;
    }
    if (
      column.dataIndex === 'attending_presentation' ||
      column.dataIndex === 'attending_dinner'
    ) {
      return record.is_reps;
    }

    if (
      column.dataIndex === 'food_option' ||
      column.dataIndex === 'registration_type'
    ) {
      return !record.is_sponsor;
    }
    // Department & Job Title editable only for Japan local chapter events
    if (
      column.dataIndex === 'attendee_department' ||
      column.dataIndex === 'attendee_job_title'
    ) {
      return !record.is_reps && !record.is_sponsor && isJapanLocalChapter;
    }
    // Add conditions for other columns if needed
    return column.editable ? true : false; // Default to editable for other columns
  };

  const adminSponsorshipPerk =
    eventOptionsByType?.sponsorship?.find(
      (option) =>
        option?.eventSponsorshipPerk || option?.event_sponsorship_perk,
    )?.eventSponsorshipPerk ||
    eventOptionsByType?.sponsorship?.find(
      (option) =>
        option?.eventSponsorshipPerk || option?.event_sponsorship_perk,
    )?.event_sponsorship_perk ||
    null;

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      sorter: true,
      fixed: isMobile ? '' : 'left',
      width: '5%',
    },

    {
      title: 'Member Name',
      dataIndex: 'user_name',
      sorter: true,
      editable: false,
      width: '10%',
      render: (text, record) => {
        return <span>{text || 'N/A'}</span>;
      },
    },
    {
      title: 'Member Email',
      dataIndex: 'user_email',
      sorter: true,
      editable: false,
      width: '10%',
      render: (text, record) => {
        return <span>{text || 'N/A'}</span>;
      },
    },

    {
      title: 'Attendee First Name',
      dataIndex: 'attendee_first_name',
      sorter: true,
      editable: true,
      width: '10%',
      render: (text, record) => {
        return <CommonTableColumnTextView columnText={text} isToolTip={true} />;
      },
    },
    {
      title: 'Attendee Last Name',
      dataIndex: 'attendee_last_name',
      width: '10%',
      editable: true,
      sorter: true,
      render: (text, record) => {
        return <span>{text || 'N/A'}</span>;
      },
    },
    {
      title: 'Email',
      dataIndex: 'attendee_email',
      editable: true,
      sorter: true,
      width: '12%',
      render: (text) => {
        return <span>{text}</span>;
      },
    },
    {
      title: 'Attendee Type',
      dataIndex: 'type',
      editable: false,
      sorter: false,
      width: '10%',
      render: (text, record) => {
        const renderType = (record) => {
          let typeLabel;
          if (record?.is_reps) {
            typeLabel = 'Vendor Reps';
          } else if (record?.is_sponsor) {
            typeLabel = record?.event_registration_options?.sponsorship?.name;
          } else {
            typeLabel = 'Regular';
          }
          return typeLabel;
        };
        return <div>{renderType(record)}</div>;
      },
    },
    {
      title: 'Company',
      dataIndex: 'attendee_company',
      editable: true,
      width: '10%',
      sorter: true,
      render: (text) => {
        return <span>{text}</span>;
      },
    },
    {
      title: 'Department',
      dataIndex: 'attendee_department',
      editable: true,
      width: '12%',
      sorter: true,
      render: (text) => {
        return <span>{text || 'N/A'}</span>;
      },
    },
    {
      title: 'Job Title',
      dataIndex: 'attendee_job_title',
      editable: true,
      width: '12%',
      sorter: true,
      render: (text) => {
        return <span>{text || 'N/A'}</span>;
      },
    },
    {
      title: 'User Local Chapter',
      dataIndex: 'user_local_chapter',
      width: '6%',
    },
    {
      title: 'Registration Type',
      dataIndex: 'registration_type',
      editable: true,
      width: '12%',
      render: (text) => {
        const typeEnum = {
          in_person: 'In Person',
          webcast: 'Webcast',
          Workshop: 'Workshop',
        };
        return <span>{typeEnum[text] || text || 'N/A'}</span>;
      },
    },
    {
      title: 'Registration Option',
      dataIndex: 'registration_option',
      width: '12%',
      render: (text, record) => {
        const isOptionsSponsor =
          record.event_registration_options?.sponsorship?.name;
        return (
          <span>
            {/* {checkIsEditable(record)} */}
            {record?.is_reps || record?.is_sponsor
              ? 'N/A'
              : isOptionsSponsor
              ? isOptionsSponsor
              : record.event_registration_options?.registration?.name +
                ' ' +
                '-' +
                ' ' +
                formatCurrencyByLocalChapter(
                  record?.event_registration_options?.registration?.price || 0,
                  localChapterData,
                )}
          </span>
        );
      },
    },
    {
      title: 'Lunch',
      editable: true,
      dataIndex: 'food_option',
      width: '10%',
      render: (text, record) => {
        const isOptionsFood = record.event_registration_options?.food?.name;
        const isOptionsPrice = record?.event_registration_options?.food?.price;
        return (
          <span>
            {isOptionsFood
              ? isOptionsFood +
                ' ' +
                '-' +
                ' ' +
                formatCurrencyByLocalChapter(
                  isOptionsPrice || 0,
                  localChapterData,
                )
              : 'No Food'}
          </span>
        );
      },
    },
    {
      title: meetingPresentationLabel(
        adminSponsorshipPerk?.attendees_price,
        ApplicationString,
      ),
      dataIndex: 'attending_presentation',
      key: 'attending_presentation',
      width: '8%',
      editable: true,
      render: (text) => {
        return (
          <div className='checkbox-editableCell'>{text ? 'Yes' : 'No'}</div>
        );
      },
    },
    {
      title: attendingDinnerLabel(
        adminSponsorshipPerk?.dinner_with_speaker_price,
        ApplicationString,
      ),
      dataIndex: 'attending_dinner',
      key: 'attending_dinner',
      width: '5%',
      editable: true,
      render: (text) => {
        return (
          <div className='checkbox-editableCell'>{text ? 'Yes' : 'No'}</div>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: '12%',
      editable: true,
      render: (status) => {
        const {color, label} = getStatusInfoOfEventRegistration(
          status,
          ApplicationString,
        );
        return <span style={{color: color}}>{label}</span>;
      },
    },
    {
      title: 'Coupon Code',
      dataIndex: 'discount_code',
      width: '6%',
      render: (text) => ReturnNAOnEmpty(text),
    },
    {
      title: 'Total Cost',
      dataIndex: 'total_cost',
      sorter: false,
      width: '6%',
      render: (text) => {
        return (
          <div className='total-cost'>
            {formatCurrencyByLocalChapter(text ? text : 0, localChapterData)}
          </div>
        );
      },
    },
    {
      title: 'Discounted Amount',
      dataIndex: 'discount_amount',
      sorter: false,
      width: '6%',
      render: (text, record) => {
        return (
          <div className='total-cost'>
            {formatCurrencyByLocalChapter(text ? text : 0, localChapterData)}
          </div>
        );
      },
    },
    {
      title: 'Paid Amount (Includes Processing Fee)',
      dataIndex: 'paid_amount',
      sorter: false,
      width: '8%',
      render: (text, record) => {
        const processingFee = getProcessingFee(record);
        const paidAmountWithProcessingFee =
          getPaidAmountWithProcessingFee(record);

        return (
          <Tooltip
            color='#fff'
            overlayInnerStyle={{color: '#111'}}
            title={
              processingFee > 0
                ? `Processing fee added: ${formatCurrencyByLocalChapter(
                    processingFee,
                    localChapterData,
                    2,
                    2,
                  )}`
                : 'No processing fee added.'
            }>
            <div className='total-cost'>
              {formatCurrencyByLocalChapter(
                paidAmountWithProcessingFee,
                localChapterData,
                2,
                2,
              )}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Processing Fee',
      dataIndex: 'processing_fee',
      sorter: false,
      width: '6%',
      render: (text, record) => {
        return (
          <div className='total-cost'>
            {formatCurrencyByLocalChapter(
              getProcessingFee(record),
              localChapterData,
              2,
              2,
            )}
          </div>
        );
      },
    },
    {
      title: 'Credit Amount',
      dataIndex: 'credit_amount',
      sorter: false,
      width: '6%',
      render: (text, record) => {
        return (
          <div className='total-cost'>
            {formatCurrencyByLocalChapter(text ? text : 0, localChapterData)}
          </div>
        );
      },
    },
    {
      title: 'Refunded Amount',
      dataIndex: 'refunded_amount',
      sorter: false,
      width: '6%',
      render: (text, record) => {
        return (
          <div className='total-cost'>
            {formatCurrencyByLocalChapter(text ? text : 0, localChapterData)}
          </div>
        );
      },
    },
    {
      title: 'Refund Method',
      dataIndex: 'refund_method',
      sorter: false,
      width: '10%',
      render: (text, record) => {
        return (
          <div className='total-cost'>
            {record?.refunded_amount > 0
              ? record?.refund_to_account
                ? 'Refund in PBSS Credit'
                : 'Refund in Bank Account'
              : 'N/A'}
          </div>
        );
      },
    },
    {
      title: 'Registration Date',
      dataIndex: 'registration_date',
      sorter: true,
      width: '12%',
      render: (value) => {
        if (!value) return '-';
        return new Date(value).toLocaleString();
      },
    },
    {
      title: 'Stripe Payment ID',
      dataIndex: 'payment_id',
      width: '14%',
      render: (text) => <span>{text || 'N/A'}</span>,
    },
    {
      title: 'Credit Card Number',
      dataIndex: 'card_last4',
      width: '8%',
      render: (text) => <span>{text ? `**** **** **** ${text}` : 'N/A'}</span>,
    },
    {
      title: 'Stripe Payment Date',
      dataIndex: 'payment_date',
      width: '12%',
    },
    {
      title: 'Billing Country',
      dataIndex: 'payment_country',
      width: '8%',
      render: (text) => <span>{text || 'N/A'}</span>,
    },
    {
      title: 'Billing ZIP',
      dataIndex: 'payment_zip',
      width: '8%',
      render: (text) => <span>{text || 'N/A'}</span>,
    },
    {
      title: 'Note',
      dataIndex: 'note',
      width: '12%',
      render: (text) => {
        return <span>{text || 'N/A'}</span>;
      },
    },
    {
      title: '',
      key: 'action',
      fixed: isMobile ? '' : 'right',
      width: '12%',
      render: (_, record) => (
        <AdminActionsDropdown
          editBool={true}
          addNoteBool={true}
          handleAddNoteClick={handleOpenAddNoteModal}
          record={record}
          usedForTable
          viewBool={true}
          handleView={showDrawer}
          qualificationFormBool={
            getQualificationFormMeta(record).requiresForm ||
            !!record?.qualification_form_submitted_at ||
            !!record?.qualification_form_answers?.length
          }
          handleQualificationFormClick={showQualificationFormModal}
          usedForAttendeeTable={true}
          handleCancelClick={cancel}
          attendeeEditingKey={editingKey}
          handleEditClick={edit}
          handleSaveClick={(record, event) => {
            if (savingRegistrationRef.current) {
              return;
            }
            const button = event?.currentTarget;
            if (button) {
              button.disabled = true;
              savingButtonRef.current = button;
            }
            save(record);
          }}
          disableSaveClicks={isSavingRegistration}
          saveLoading={isSavingRegistration}
          text='Registration'
          usedForAdminSide={true}
          resource='events_registrations'
          deleteBool={record.status === EventRegistrationStatus.TO_BE_PAID}
          handleDeleteClick={(registrationId) =>
            deleteToBePaidRegistration(registrationId, () =>
              fetchEventRegistrations(searchValues),
            )
          }
        />
      ),
    },
  ];
  columns.forEach((column) => {
    if (column.key !== 'action') {
      column.sortOrder =
        sortParams.field === column.dataIndex && sortParams.order;
    }
  });

  // Sponsors tab: first column says what each row is, so sponsors and their reps are easy to tell apart.
  const sponsorTabColumns = isSponsorsTab
    ? [
        {
          title: 'Sponsor / Vendor Rep',
          dataIndex: 'payment_group_kind',
          fixed: isMobile ? '' : 'left',
          width: 230,
          render: (_, record) =>
            record?.is_sponsor ? (
              <div className='sponsor-tree-cell'>
                <Tag color='blue' className='sponsor-tree-tag'>
                  SPONSOR
                </Tag>
                <div className='sponsor-tree-company'>
                  {record?.attendee_company || 'N/A'}
                </div>
                <div className='sponsor-tree-sub'>
                  {record?.rep_count
                    ? `${record.rep_count} vendor rep${
                        record.rep_count > 1 ? 's' : ''
                      }${
                        record?.added_rep_count
                          ? ` (${record.added_rep_count} added later)`
                          : ''
                      }`
                    : 'No vendor reps'}
                </div>
              </div>
            ) : (
              <div className='sponsor-tree-cell'>
                <Tag color='green' className='sponsor-tree-tag'>
                  VENDOR REP
                </Tag>
                <div>
                  {record?.payment_group_kind === 'added' ? (
                    <Tag color='orange'>
                      Added later · Payment {record?.payment_group_number}
                    </Tag>
                  ) : (
                    <Tag>With original purchase</Tag>
                  )}
                </div>
                {record?.payment_group_kind === 'added' &&
                  record?.payment_group_date && (
                    <div className='sponsor-tree-sub'>
                      Paid on {formatDate(record.payment_group_date)}
                    </div>
                  )}
              </div>
            ),
        },
      ]
    : [];

  const mergedColumns = [...sponsorTabColumns, ...columns].map((col) => {
    return {
      ...col,
      onCell: (record) => ({
        record,
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record) && shouldEditCell(col, record),
        options: col.dataIndex,
      }),
    };
  });

  const getRefundMethod = (item) => {
    if (item?.refunded_amount > 0) {
      return item?.refund_to_account
        ? 'Refund in PBSS Credit'
        : 'Refund in Bank Account';
    }
    return 'N/A';
  };

  // A plain dollar prefix keeps Excel from dropping trailing cents (2047 → 2047, 53.30 → 53.3).
  const csvAmount = (value) => `$${Number(value || 0).toFixed(2)}`;

  const qualificationQuestionColumns = buildQualificationQuestionColumns(
    allEventRegistrationDataForCSV,
  );

  const formateEventRegistrationData = () => {
    let newEventRegistrations = [];
    allEventRegistrationDataForCSV?.map((item) => {
      const processingFee = getProcessingFee(item);
      const paidAmountWithProcessingFee = getPaidAmountWithProcessingFee(item);
      totalProcessingFeeForCSV += processingFee;
      totalPaidAmountWithProcessingFeeForCSV += paidAmountWithProcessingFee;
      let newEventRegistration = {
        id: item.id,
        user_id: item?.user_id,
        user_name: item?.user_name,
        user_email: item.user_email,
        event_id: item.event_id,
        attendee_first_name: item?.attendee_first_name || '',
        attendee_last_name: item?.attendee_last_name || '',
        attendee_name: [item?.attendee_first_name, item?.attendee_last_name]
          .map((namePart) => (namePart || '').trim())
          .filter(Boolean)
          .join(' '),
        attendee_email: item.attendee_email,
        attendee_company: item.attendee_company,
        attendee_department: item.attendee_department || 'N/A',
        attendee_job_title: item.attendee_job_title || 'N/A',
        user_local_chapter: item.user_local_chapter,
        is_sponsor: item?.is_sponsor ? 'Yes' : 'No',
        is_reps: item?.is_reps ? 'Yes' : 'No',
        sponsorship:
          item?.event_registration_options?.sponsorship?.name || 'NA',
        registration_type: item?.registration_type,
        registration: item?.event_registration_options?.registration?.name,
        food: item?.event_registration_options?.food?.name || 'No Food',
        status: getStatusName(item.status),
        total_cost: csvAmount(item?.total_cost),
        discount_code: item?.discount_code || 'N/A',
        discount_amount: csvAmount(item?.discount_amount),
        paid_amount: csvAmount(paidAmountWithProcessingFee),
        processing_fee: csvAmount(processingFee),
        credit_amount: csvAmount(item?.credit_amount),
        refunded_amount: csvAmount(item?.refunded_amount),
        refund_method: getRefundMethod(item),
        registration_date: item?.registration_date,
        payment_id: item?.payment_id || 'N/A',
        payment_country: item?.payment_country || 'N/A',
        payment_zip: item?.payment_zip || 'N/A',
        payment_date: item?.payment_date || '',
        card_last4: item?.card_last4
          ? `**** **** **** ${item.card_last4}`
          : 'N/A',
        attending_presentation: item?.attending_presentation ? 'Yes' : 'No',
        attending_dinner: item?.attending_dinner ? 'Yes' : 'No',
        qualification_form: getQualificationFormMeta(item).label,
        qualification_form_submitted_at: item?.qualification_form_submitted_at
          ? formatUtcToLocalDateTime(item.qualification_form_submitted_at) ||
            item.qualification_form_submitted_at
          : 'N/A',
        tally_submission_id: item?.tally_submission_id || 'N/A',
        ...getQualificationQuestionAnswers(
          item?.qualification_form_answers,
        ).reduce((cells, {id, value}) => {
          const column = qualificationQuestionColumns.get(id);
          if (column) cells[column.key] = value;
          return cells;
        }, {}),
        attendee_type: item?.is_reps
          ? 'Vendor Reps'
          : item?.is_sponsor
          ? item?.event_registration_options?.sponsorship?.name || 'NA'
          : 'Regular',
        note: item?.note || 'N/A',
      };
      item?.status === EventRegistrationStatus.PAID ||
      item?.status === EventRegistrationStatus.PAYMENT_CONFIRMED ||
      item?.status === EventRegistrationStatus.FEE_WAIVER ||
      item?.status === EventRegistrationStatus.NO_CHARGE_EVENT
        ? (totalIncome = totalIncome + Number(item?.total_cost))
        : (totalIncome = totalIncome + 0);
      totalDiscounted = totalDiscounted + Number(item?.discount_amount);
      newEventRegistrations.push(newEventRegistration);
    });

    return newEventRegistrations;
  };
  const formattedEventRegistrationData = formateEventRegistrationData();

  const headers = [
    {label: 'Id', key: 'id'},
    {label: 'User', key: 'user_id'},
    {label: 'Member Name', key: 'user_name'},
    {label: 'Member Email', key: 'user_email'},
    {label: 'Event ID', key: 'event_id'},
    {label: 'Attendee Name', key: 'attendee_name'},
    {label: 'Attendee Email', key: 'attendee_email'},
    {label: 'Attendee Company', key: 'attendee_company'},
    {label: 'Attendee Department', key: 'attendee_department'},
    {label: 'Attendee Job Title', key: 'attendee_job_title'},
    {label: 'User Local Chapter', key: 'user_local_chapter'},
    {label: 'Sponsor', key: 'is_sponsor'},
    {label: 'Representative', key: 'is_reps'},
    {label: 'Registration Type', key: 'registration_type'},
    {label: 'Registration Option', key: 'registration'},
    {label: 'Food', key: 'food'},
    {label: 'Registration Date', key: 'registration_date'},
    {label: 'Status', key: 'status'},
    {label: 'Coupon Code', key: 'discount_code'},
    {label: 'Refund Method', key: 'refund_method'},
    {
      label: meetingPresentationLabel(
        adminSponsorshipPerk?.attendees_price,
        ApplicationString,
      ),
      key: 'attending_presentation',
    },
    {
      label: attendingDinnerLabel(
        adminSponsorshipPerk?.dinner_with_speaker_price,
        ApplicationString,
      ),
      key: 'attending_dinner',
    },
    {label: 'Qualification Form', key: 'qualification_form'},
    {
      label: 'Qualification Submitted At',
      key: 'qualification_form_submitted_at',
    },
    {label: 'Tally Submission ID', key: 'tally_submission_id'},
    ...Array.from(qualificationQuestionColumns.values()),
    {label: 'Attendee Type', key: 'attendee_type'},
    {label: 'Note', key: 'note'},
    {label: 'Stripe Payment ID', key: 'payment_id'},
    {label: 'Credit Card Number', key: 'card_last4'},
    {label: 'Billing Country', key: 'payment_country'},
    {label: 'Billing ZIP', key: 'payment_zip'},
    {label: 'Stripe Payment Date', key: 'payment_date'},
    {label: 'Total Cost', key: 'total_cost'},
    {label: 'Discounted Amount', key: 'discount_amount'},
    {label: 'Paid Amount (Includes Processing Fee)', key: 'paid_amount'},
    {label: 'Processing Fee', key: 'processing_fee'},
    {label: 'Credit Amount', key: 'credit_amount'},
    {label: 'Refunded Amount', key: 'refunded_amount'},
  ];
  const totalPaidAmountForCSV =
    totalPaidAmountWithProcessingFeeForCSV || totalPaidAmount;
  const totalCost =
    totalPaidAmountForCSV + totalCreditAmount - totalRefundAmount;
  formattedEventRegistrationData.push(
    {
      id: '',
      user_id: '',
      user_name: '',
      user_email: '',
      event_id: '',
      attendee_first_name: '',
      attendee_last_name: '',
      attendee_name: '',
      attendee_email: '',
      attendee_company: '',
      attendee_department: '',
      attendee_job_title: '',
      user_local_chapter: '',
      is_sponsor: '',
      is_reps: '',
      sponsorship: '',
      registration_type: '',
      registration: '',
      food: '',
      registration_date: '',
      status: '',
      discount_code: '',
      refund_method: '',
      note: '',
      attending_presentation: '',
      attending_dinner: '',
      attendee_type: '',
      total_cost: csvAmount(total_Amount),
      discount_amount: csvAmount(totalDiscountedAmount),
      paid_amount: csvAmount(totalPaidAmountForCSV),
      processing_fee: csvAmount(totalProcessingFeeForCSV),
      credit_amount: csvAmount(totalCreditAmount),
      refunded_amount: csvAmount(totalRefundAmount),
    },
    {},
    {
      note: '',
      total_cost: `Total Paid Amount: ${csvAmount(totalCost)}`,
    },
    {
      total_cost: `(Included Credit Amount)`,
    },
  );

  return (
    <div className='main-container'>
      {isAddNoteModalVisible && (
        <AddNoteModal
          record={allRowsOnPage?.find(
            (item) => item.id === selectedEventRegistration.note_id,
          )}
          ApplicationString={ApplicationString}
          visible={isAddNoteModalVisible}
          onCancel={handleAddNoteModalCancel}
          onOk={handleAddNoteModalOk}
          noteValue={noteValue}
          setNoteValue={setNoteValue}
          selectedEventRegistrationValue={selectedEventRegistration.note_id}
        />
      )}
      {qualificationFormModalVisible && (
        <QualificationFormModal
          visible={qualificationFormModalVisible}
          onCancel={closeQualificationFormModal}
          record={qualificationFormRecord}
          qualificationMeta={getQualificationFormMeta(qualificationFormRecord)}
        />
      )}
      <AdminViewDetailsDrawer
        content={EventRegistrationDrawerContent}
        visible={open}
        text={'Event Registration'}
        onCloseDrawer={onCloseDrawer}
        isEditable={false}
      />
      <div className='search-container' style={{gap: '10px'}}>
        <Button
          onClick={() => navigate('/admin/events')}
          id='EventTemplateSchedulerComponent-button-filterSearch'>
          Back To Events
        </Button>
        <Button
          onClick={toggleExpand}
          id='EventRegistration-button-filterSearch'>
          {expanded ? <FilterOutlined /> : <FilterOutlined />}{' '}
          {ApplicationString['dynamicSearch.button.heading']}
        </Button>
        {csvDataLoading && !allEventRegistrationDataForCSV && (
          <Button>Fetching Attendees..</Button>
        )}
        {!csvDataLoading && !allEventRegistrationDataForCSV?.length && (
          <Button
            csvDataLoading
            onClick={() => {
              fetchEventRegistrationsForCSV(searchValues);
            }}>
            Load CSV Data
          </Button>
        )}
        {!csvDataLoading && allEventRegistrationDataForCSV?.length > 0 && (
          <Button id='Event-register-button-downloadCsv'>
            <CSVLink
              data={formattedEventRegistrationData}
              headers={headers}
              filename={'Event_Report.csv'}>
              Download CSV
            </CSVLink>
          </Button>
        )}
      </div>
      {expanded && (
        <DynamicSearchForm
          columns={eventRegistrationsFilters}
          onSearch={handleSearch}
          savedFilteredValues={searchValues}
          title={'event_registrations'}
        />
      )}
      <Tabs
        activeKey={registrationTab}
        onChange={(tab) => {
          setSelectedRowKeys([]);
          setExpandedSponsorKeys(null);
          onRegistrationTabChange && onRegistrationTabChange(tab);
        }}
        items={[
          {
            key: EventRegistrationAdminTab.registrations,
            label: 'Registrations',
          },
          {key: EventRegistrationAdminTab.sponsors, label: 'Sponsors'},
        ]}
      />
      {isSponsorsTab && (
        <div className='sponsor-tree-toolbar'>
          <Alert
            type='info'
            showIcon
            className='sponsor-tree-legend'
            message='Each sponsor is listed with its vendor reps underneath.'
            description={
              <span>
                <Tag color='blue'>SPONSOR</Tag> the sponsorship purchase.{' '}
                <Tag color='green'>VENDOR REP</Tag> a rep under that sponsor:{' '}
                <Tag>With original purchase</Tag> bought with the sponsorship,{' '}
                <Tag color='orange'>Added later</Tag> added afterwards from My
                Events as a separate payment. Selecting a sponsor also selects
                its vendor reps. Cancelling or refunding a sponsor also cancels
                or refunds all of its vendor reps.
              </span>
            }
          />
        </div>
      )}
      <div className='listing-container'>
        <Form form={form} component={false}>
          <Table
            bordered
            className='admin-table'
            components={{
              body: {
                cell: (props) => (
                  <EditableCell
                    foodOptions={foodOptions}
                    registrationOptions={registrationOptions}
                    handleAttendeePresentationChange={
                      handleAttendeePresentationChange
                    }
                    checkedAttendeePresentation={attendeePresentationChecked}
                    handleAttendeeDinnerChange={handleAttendeeDinnerChange}
                    checkedAttendeeDinner={attendeeDinnerChecked}
                    {...props}
                  />
                ),
              },
            }}
            rowSelection={
              isCancelAndRefundAccessed
                ? {
                    type: selectionType,
                    ...rowSelection,
                  }
                : undefined
            }
            footer={footer}
            columns={mergedColumns}
            dataSource={tableRows}
            rowKey={'id'}
            rowClassName={(record) =>
              isSponsorsTab
                ? record?.is_sponsor
                  ? 'sponsor-tree-sponsor-row'
                  : 'sponsor-tree-rep-row'
                : ''
            }
            expandable={
              isSponsorsTab
                ? {
                    expandedRowKeys: expandedSponsorKeys ?? [],
                    indentSize: 24,
                    onExpandedRowsChange: (keys) =>
                      setExpandedSponsorKeys(keys),
                  }
                : undefined
            }
            onChange={handleTableChange}
            sticky={true}
            scroll={{x: 1800}}
            pagination={{
              onChange: handlePaginationChange,
              current: pn || paging.page,
              total: paging.totalCount,
              pageSize: ps || paging.pageCount,
              pageSizeOptions: PageSizeArray,
              showQuickJumper: true,
              showSizeChanger: true,
              onShowSizeChange: handlePaginationChange,
              showTotal: (total, range) =>
                `Credit Amount: ${formatCurrencyByLocalChapter(
                  totalCreditAmount,
                  'USD',
                )} | Paid Amount: ${formatCurrencyByLocalChapter(
                  totalPaidAmount,
                  'USD',
                )} | Discounted Amount: ${formatCurrencyByLocalChapter(
                  totalDiscountedAmount,
                  'USD',
                )} | Total Amount: ${formatCurrencyByLocalChapter(
                  total_Amount,
                  'USD',
                )} | Total ${total} items`,
              position: [PaginationPosition],
            }}
          />
        </Form>
      </div>
    </div>
  );
};

const EditableCell = ({
  editing,
  dataIndex,
  title,
  record,
  index,
  children,
  options,
  foodOptions,
  registrationOptions,
  handleAttendeePresentationChange,
  checkedAttendeePresentation,
  handleAttendeeDinnerChange,
  checkedAttendeeDinner,
  ...restProps
}) => {
  const inputNode = (() => {
    switch (dataIndex) {
      case 'food_option':
        return (
          <ViewportSelect
            dropdownStyle={{minWidth: 220}}
            placeholder={`Select your options for ${title}`}>
            {foodOptions?.map((foodOption) => (
              <Option
                key={foodOption.label}
                value={foodOption.value}
                disabled={foodOption?.qty - foodOption?.used_qty === 0}>
                {foodOption.label}
              </Option>
            ))}
          </ViewportSelect>
        );
      case 'status':
        return (
          <ViewportSelect
            dropdownStyle={{minWidth: 130}}
            placeholder={`Select your status for ${title}`}>
            {registrationFilterOptions?.map((regOption) => (
              <Option
                key={regOption.label}
                value={regOption.value}
                disabled={regOption.value === 0 || regOption.value === -1}>
                {regOption.label}
              </Option>
            ))}
          </ViewportSelect>
        );
      case 'registration_option':
        return (
          <ViewportSelect
            dropdownStyle={{minWidth: 350}}
            placeholder={`Select your options for ${title}`}>
            {registrationOptions?.map((regOption) => (
              <Option
                key={regOption.label}
                value={regOption.value}
                disabled={regOption?.qty - regOption?.used_qty === 0}>
                {regOption.label}
              </Option>
            ))}
          </ViewportSelect>
        );
      case 'registration_type':
        return (
          <ViewportSelect
            dropdownStyle={{minWidth: 130}}
            placeholder={`Select your options for ${title}`}
            options={event_registration_type}></ViewportSelect>
        );
      case 'attendee_email':
        return (
          <Form.Item
            name={dataIndex}
            className='no-margin'
            rules={[
              {
                required: true,
                message: `Please fill out ${title?.toLowerCase()}`,
              },
              {
                type: 'email',
                message: 'Please enter a valid email address',
              },
            ]}>
            <Input
              onCopy={(e) => e.preventDefault()}
              placeholder={`${`Enter attendee ${title?.toLowerCase()}`}`}
            />
          </Form.Item>
        );
      case 'attending_presentation':
        return (
          <Form.Item name={dataIndex} style={{margin: 0}}>
            <div className='checkbox-editableCell'>
              <Checkbox
                onChange={handleAttendeePresentationChange}
                checked={checkedAttendeePresentation}></Checkbox>
            </div>
          </Form.Item>
        );
        break;
      case 'attending_dinner':
        return (
          <Form.Item name={dataIndex} style={{margin: 0}}>
            <div className='checkbox-editableCell'>
              <Checkbox
                onChange={handleAttendeeDinnerChange}
                checked={checkedAttendeeDinner}></Checkbox>
            </div>
          </Form.Item>
        );
        break;
      case 'attendee_company':
        return (
          <Form.Item
            name={dataIndex}
            rules={[
              {
                required: true,
                message: `Please fill out ${title?.toLowerCase()}`,
              },
            ]}
            style={{margin: 0}}>
            <Input
              placeholder={`${`Enter attendee ${title?.toLowerCase()}`}`}
            />
          </Form.Item>
        );
        break;
      case 'attendee_department':
      case 'attendee_job_title':
        return (
          <Form.Item
            name={dataIndex}
            rules={[
              {
                required: true,
                message: `Please fill out ${title?.toLowerCase()}`,
              },
            ]}
            style={{margin: 0}}>
            <Input
              placeholder={`${`Enter attendee ${title?.toLowerCase()}`}`}
            />
          </Form.Item>
        );
      default:
        return (
          <Form.Item
            name={dataIndex}
            rules={[
              {
                required: true,
                message: `Please fill out ${title?.toLowerCase()}`,
              },
              {
                validator: (_, value) =>
                  OnlyAlphabetInputValidatorWithSpecialCharacters(value, title),
              },
            ]}
            style={{margin: 0}}>
            <Input
              placeholder={`${`Enter attendee ${title?.toLowerCase()}`}`}
            />
          </Form.Item>
        );
    }
  })();

  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{margin: 0}}
          rules={[
            {
              required: dataIndex === 'registration_type' ? false : true,

              message: '',
            },
          ]}>
          {inputNode}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

export default EventRegistrationsComponent;
